/**
 * SeatingMapEditor.jsx
 * Full-screen dialog for editing the synagogue seating map.
 * Rows and landmarks are positioned by dragging directly on the SVG canvas.
 */
import React, { useState, useCallback } from 'react';
import {
  Dialog, Box, Button, Typography, IconButton, AppBar, Toolbar,
  Divider, Slider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import MapCanvas from '../../business/SeatingMapWidget/MapCanvas';
import RowManager from './RowManager';
import SeatNameEditor from './SeatNameEditor';
import LandmarkControls from './LandmarkControls';
import {
  createDefaultMapData,
  migrateMapData,
  addRow,
  removeRow,
  duplicateRow,
  updateRowSeatCount,
  updateRowPosition,
  updateRowSeats,
  updateLandmark,
  DEFAULT_SEAT_SIZE,
} from '../../business/SeatingMapWidget/seatingMapUtils';

const navy = '#1e3a5f';

const SeatingMapEditor = ({ open, onClose, initialMapData, onSave }) => {
  const [mapData, setMapData] = useState(() => migrateMapData(initialMapData) || createDefaultMapData());

  // Seat name editor state
  const [seatEditorOpen, setSeatEditorOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // Reset state when dialog opens with new data
  React.useEffect(() => {
    if (open) {
      setMapData(migrateMapData(initialMapData) || createDefaultMapData());
    }
  }, [open, initialMapData]);

  // ─── General settings ───────────────────────────────────────────────

  const handleUpdateSeatSize = useCallback((value) => {
    setMapData(prev => ({ ...prev, seatSize: value }));
  }, []);

  const handleUpdateWidth = useCallback((value) => {
    setMapData(prev => ({ ...prev, width: value }));
  }, []);

  const handleUpdateHeight = useCallback((value) => {
    setMapData(prev => ({ ...prev, height: value }));
  }, []);

  // ─── Row operations ─────────────────────────────────────────────────

  const handleAddRow = useCallback(() => {
    setMapData(prev => addRow(prev));
  }, []);

  const handleRemoveRow = useCallback((rowId) => {
    setMapData(prev => removeRow(prev, rowId));
  }, []);

  const handleDuplicateRow = useCallback((rowId) => {
    setMapData(prev => duplicateRow(prev, rowId));
  }, []);

  const handleUpdateSeatCount = useCallback((rowId, newCount) => {
    setMapData(prev => updateRowSeatCount(prev, rowId, newCount));
  }, []);

  // ─── Row drag on canvas ─────────────────────────────────────────────

  const handleRowUpdate = useCallback((rowId, { x, y }) => {
    setMapData(prev => updateRowPosition(prev, rowId, x, y));
  }, []);

  // ─── Seat naming ────────────────────────────────────────────────────

  const handleOpenSeatNames = useCallback((row) => {
    setEditingRow(row);
    setSeatEditorOpen(true);
  }, []);

  const handleSaveSeatNames = useCallback((updatedSeats) => {
    if (!editingRow) return;
    setMapData(prev => updateRowSeats(prev, editingRow.id, updatedSeats));
  }, [editingRow]);

  // Seat click on map (from short-click, no drag)
  const handleSeatClick = useCallback((seat) => {
    const row = mapData.rows?.find(r => r.id === seat.rowId);
    if (row) handleOpenSeatNames(row);
  }, [mapData.rows, handleOpenSeatNames]);

  // ─── Landmark operations ────────────────────────────────────────────

  const handleUpdateLandmark = useCallback((landmarkId, updates) => {
    setMapData(prev => updateLandmark(prev, landmarkId, updates));
  }, []);

  // ─── Save ───────────────────────────────────────────────────────────

  const handleSave = () => {
    onSave(mapData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { direction: 'rtl', bgcolor: '#f8fafc' } }}
    >
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: navy }}>
        <Toolbar sx={{ minHeight: '48px !important', gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
            עורך מפת מושבים
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, fontWeight: 600 }}
          >
            שמור
          </Button>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Body: Sidebar + Map */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <Box
          sx={{
            width: 280,
            minWidth: 280,
            borderLeft: '1px solid #e2e8f0',
            bgcolor: '#fff',
            overflowY: 'auto',
            p: 2,
          }}
        >
          {/* General settings */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: navy }}>
            הגדרות כלליות
          </Typography>

          {/* Wall width */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 0.5 }}>
            <Typography variant="caption" sx={{ minWidth: 36, color: '#888', fontSize: 10 }}>רוחב</Typography>
            <Slider
              size="small" min={20} max={80} step={1}
              value={mapData.width}
              onChange={(_, v) => handleUpdateWidth(v)}
              sx={{ flex: 1, color: navy }}
            />
            <Typography variant="caption" sx={{ minWidth: 20, textAlign: 'center', color: '#555' }}>{mapData.width}</Typography>
          </Box>

          {/* Wall height */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 0.5 }}>
            <Typography variant="caption" sx={{ minWidth: 36, color: '#888', fontSize: 10 }}>גובה</Typography>
            <Slider
              size="small" min={15} max={60} step={1}
              value={mapData.height}
              onChange={(_, v) => handleUpdateHeight(v)}
              sx={{ flex: 1, color: navy }}
            />
            <Typography variant="caption" sx={{ minWidth: 20, textAlign: 'center', color: '#555' }}>{mapData.height}</Typography>
          </Box>

          {/* Seat size */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 0.5, mb: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 36, color: '#888', fontSize: 10 }}>מושב</Typography>
            <Slider
              size="small" min={0.4} max={2} step={0.1}
              value={mapData.seatSize ?? DEFAULT_SEAT_SIZE}
              onChange={(_, v) => handleUpdateSeatSize(v)}
              sx={{ flex: 1, color: navy }}
            />
            <Typography variant="caption" sx={{ minWidth: 20, textAlign: 'center', color: '#555' }}>{mapData.seatSize ?? DEFAULT_SEAT_SIZE}</Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Row manager */}
          <RowManager
            rows={mapData.rows || []}
            onAddRow={handleAddRow}
            onDuplicateRow={handleDuplicateRow}
            onRemoveRow={handleRemoveRow}
            onUpdateSeatCount={handleUpdateSeatCount}
            onOpenSeatNames={handleOpenSeatNames}
            onHoverRow={setHighlightedRowId}
          />

          {/* Landmark visibility toggles */}
          <LandmarkControls
            landmarks={mapData.landmarks}
            onUpdate={handleUpdateLandmark}
          />
        </Box>

        {/* Map canvas area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ maxWidth: 800, maxHeight: '100%', width: '100%', aspectRatio: `${mapData.width}/${mapData.height}` }}>
            <MapCanvas
              mapData={mapData}
              onSeatClick={handleSeatClick}
              onLandmarkUpdate={handleUpdateLandmark}
              onRowUpdate={handleRowUpdate}
              highlightedRowId={highlightedRowId}
              showNames
            />
          </Box>
        </Box>
      </Box>

      {/* Seat name editor dialog */}
      <SeatNameEditor
        open={seatEditorOpen}
        onClose={() => setSeatEditorOpen(false)}
        row={editingRow}
        onSave={handleSaveSeatNames}
      />
    </Dialog>
  );
};

export default SeatingMapEditor;
